import React from 'react';
import { alpha, Box, Chip, Paper, Stack, Typography, useTheme } from '@mui/material';
import { CONFIDENCE_LABEL, EdgeConfidence } from '../utils/betValue';
import { toAmericanOdds } from './OddsToggle';

/** One side of a head-to-head on the board, priced (the dialog's pick for it). */
export interface MatchupSide {
  key: string;
  driver: string;
  opponent: string;
  odds: number;
  /** Our chance of this driver finishing ahead (base model), and Track Specialists' when the run has it. */
  pBase: number;
  pLayered?: number;
  confidence: EdgeConfidence;
  edgePts: number;
  tier: 'pick' | 'small' | 'none';
  /** The price is far from anything a book would post for our chance: likely a misread. */
  implausible: boolean;
  /** The pick's buttons (add to slip / parlay). */
  actions?: React.ReactNode;
}

const fmtOdds = (o: number) => (o > 0 ? `+${o}` : `${o}`);
const pct = (p: number) => `${Math.round(p * 100)}%`;
const implied = (o: number) => (o < 0 ? -o / (-o + 100) : 100 / (o + 100));
const CONF_COLOR: Record<EdgeConfidence, 'success' | 'warning' | 'default' | 'error'> = {
  clear: 'success',
  marginal: 'warning',
  within: 'default',
  negative: 'error',
};

/** Who finishes ahead, on one 0–100% line: driver A's share filled from the left, 50% marked. */
const SplitBar: React.FC<{ label: string; p: number; color: string }> = ({ label, p, color }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography
      sx={{ width: 120, flex: 'none', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary' }}
      noWrap
    >
      {label}
    </Typography>
    <Typography sx={{ width: 36, flex: 'none', fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {pct(p)}
    </Typography>
    <Box
      sx={{ flex: 1, position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}
    >
      <Box
        sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${p * 100}%`, bgcolor: alpha(color, 0.8) }}
      />
      <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: 'text.disabled' }} />
    </Box>
    <Typography
      sx={{
        width: 36,
        flex: 'none',
        fontSize: 12.5,
        fontWeight: 700,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {pct(1 - p)}
    </Typography>
  </Stack>
);

/**
 * Every head-to-head on the scanned board, one card per matchup: both drivers with the book's price and our fair
 * price, the book's split (its margin taken out across the pair) against ours, and a verdict. Edges are against the
 * price as offered (what the bet pays); the split is only for comparing the two views of the matchup.
 */
export const BoardScanMatchups: React.FC<{ sides: MatchupSide[]; hasLayered: boolean }> = ({ sides, hasLayered }) => {
  const theme = useTheme();
  // pair the sides: one card per matchup, in the order the board listed them
  const cards: [MatchupSide, MatchupSide | undefined][] = [];
  const done = new Set<string>();
  for (const s of sides) {
    const k = [s.driver, s.opponent].sort().join('|');
    if (done.has(k)) continue;
    done.add(k);
    cards.push([s, sides.find((o) => o.driver === s.opponent && o.opponent === s.driver)]);
  }
  if (cards.length === 0) return null;
  const good = theme.palette.success.main;

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>Matchups on this board</Typography>
        <Typography variant="caption" color="text.secondary">
          who finishes ahead; a driver out of the race finishes behind one still running
        </Typography>
      </Stack>
      <Stack spacing={1.25}>
        {cards.map(([a, b]) => {
          const value = [a, b].filter((s): s is MatchupSide => !!s && s.tier === 'pick' && !s.implausible);
          const best = value.sort((x, y) => y.edgePts - x.edgePts)[0];
          const smaller = [a, b].find((s) => s && s.tier === 'small' && !s.implausible);
          const odd = [a, b].filter((s): s is MatchupSide => !!s && s.implausible);
          const side = (s: MatchupSide | undefined, driver: string, right: boolean) => (
            <Box sx={{ flex: 1, minWidth: 0, textAlign: right ? 'right' : 'left' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }} noWrap>
                {driver}
              </Typography>
              <Stack
                direction={right ? 'row-reverse' : 'row'}
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 15,
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    px: 0.75,
                    borderRadius: 1,
                    bgcolor: s && s === best ? alpha(good, 0.18) : 'action.hover',
                    color: s && s === best ? good : 'text.primary',
                  }}
                >
                  {s ? fmtOdds(s.odds) : '–'}
                </Box>
                {s && (
                  <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    ours {toAmericanOdds(s.pBase)}
                  </Typography>
                )}
                {s?.actions}
              </Stack>
            </Box>
          );
          // book split with its margin taken out across the pair (needs both sides read)
          const bookA = b ? implied(a.odds) / (implied(a.odds) + implied(b.odds)) : undefined;
          const margin = b ? implied(a.odds) + implied(b.odds) - 1 : undefined;
          return (
            <Paper
              key={a.key}
              variant="outlined"
              sx={{ p: 1.75, borderRadius: 2, borderColor: best ? alpha(good, 0.55) : undefined }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                {side(a, a.driver, false)}
                <Typography sx={{ pt: 0.5, color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>VS</Typography>
                {side(b, a.opponent, true)}
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 1.25 }}>
                {bookA !== undefined && <SplitBar label="Book" p={bookA} color={theme.palette.text.secondary} />}
                <SplitBar label={hasLayered ? 'Base model' : 'Ours'} p={a.pBase} color={theme.palette.primary.main} />
                {hasLayered && a.pLayered !== undefined && (
                  <SplitBar label="Track Specialists" p={a.pLayered} color={theme.palette.info.main} />
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1, rowGap: 0.5 }}
              >
                {best ? (
                  <>
                    <Chip size="small" color={CONF_COLOR[best.confidence]} label={CONFIDENCE_LABEL[best.confidence]} />
                    <Typography sx={{ fontSize: 13 }}>
                      <b>{best.driver}</b> at {fmtOdds(best.odds)}:{' '}
                      <Box component="span" sx={{ color: good, fontWeight: 700 }}>
                        +{best.edgePts.toFixed(1)} pts
                      </Box>{' '}
                      over the book's price
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {smaller
                      ? `${smaller.driver} is priced a little better than our odds, but within our uncertainty.`
                      : 'No side beats our uncertainty.'}
                    {!b && " Only one side was read, so the book's split isn't shown."}
                  </Typography>
                )}
                {odd.length > 0 && (
                  <Typography sx={{ fontSize: 13, color: 'warning.main' }}>
                    {odd.map((s) => `${s.driver} ${fmtOdds(s.odds)}`).join(' and ')} looks unusual for this matchup:
                    check it against your book.
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {margin !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    book margin {pct(margin)}
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};
